/**
 * Lo que la botica revisa en el almacén: lo devuelto en cuarentena y el libro
 * de recetas. Ver docs/15 §7 sexies y §2.40 (migraciones 239 y 240).
 *
 * Sin pantalla ni red: los textos y las filas del CSV salen de aquí para que
 * se prueben solos.
 */
import { hora } from './formato';

const FECHA_DE_LIMA = new Intl.DateTimeFormat('es-PE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'America/Lima',
});

/** Lo devuelto que espera revisión, o lo que ya se decidió. */
export interface EntradaDeCuarentena {
  id: string;
  supplyName: string;
  unit: string;
  lotCode: string | null;
  expiresOn: string | null;
  quantity: string;
  productName: string;
  orderNumber: number | null;
  /** Lo que marcó la caja al devolver: una opinión, no una decisión. */
  cashierSaysRestock: boolean | null;
  /**
   * De dónde vino: lo devuelto, lo anulado después de cobrar o el sobrante de
   * un conteo que no traía lote ni vencimiento (migración 269). Un servidor de
   * antes no lo manda: era siempre una devolución.
   */
  origin?: OrigenDeCuarentena;
  status: 'pending' | 'released' | 'written_off';
  createdAt: string;
  decidedAt: string | null;
  decidedBy: string | null;
  decisionNote: string | null;
}

/** Una línea vendida que pedía receta. */
export interface LineaDelLibro {
  recordedAt: string;
  orderNumber: number | null;
  documentNumber: string | null;
  productName: string;
  quantity: string;
  /** Los lotes de los que salió, separados por coma; vacío si salió sin lote. */
  lots: string;
  saleCondition: 'prescription' | 'retained_prescription';
  prescriptionNumber: string | null;
  prescriber: string | null;
  checkedBy: string;
  cancelled: boolean;
  /**
   * Que se vendió sin la receta que pedía: una venta hecha sin red que llegó
   * por la cola y entró porque ya estaba cobrada. Es la que el químico
   * farmacéutico tiene que regularizar. Un servidor de antes no lo manda.
   */
  withoutPrescription?: boolean;
}

/**
 * Lo que dijo la caja, dicho para quien revisa.
 *
 * Se enseña porque quien devolvió vio el frasco y quien revisa a veces no:
 * «está entero» orienta, pero no libera nada —eso lo decide quien firma—.
 */
export type OrigenDeCuarentena = 'refund' | 'cancelled_sale' | 'count_surplus' | 'doubtful_receipt';

/** De dónde vino lo apartado, en las palabras de quien lo revisa. */
export function origenDeCuarentena(origen: OrigenDeCuarentena | undefined): string {
  switch (origen) {
    case 'cancelled_sale':
      return 'Anulado después de cobrar';
    case 'count_surplus':
      return 'Sobrante de un conteo';
    // Migración 273: lo que llegó con el sello roto o el canje sin confirmar.
    case 'doubtful_receipt':
      return 'Recibido en duda';
    default:
      return 'Devuelto por el cliente';
  }
}

/**
 * Si liberarlo pide el lote y el vencimiento: el sobrante de un conteo no los
 * traía, y es justo lo que esperaba. Lo devuelto y lo anulado vuelven a su
 * lote de siempre.
 */
export function liberarPideLote(entrada: Pick<EntradaDeCuarentena, 'origin' | 'lotCode'>): boolean {
  return entrada.origin === 'count_surplus' && entrada.lotCode === null;
}

export function opinionDeLaCaja(restock: boolean | null): string {
  if (restock === true) return 'La caja dice que está entero';
  if (restock === false) return 'La caja dice que no vuelve';
  return 'Sin opinión de la caja';
}

/** Qué se decidió, en una palabra. */
export function estadoDeCuarentena(status: EntradaDeCuarentena['status']): string {
  switch (status) {
    case 'pending':
      return 'En revisión';
    case 'released':
      return 'Liberado al estante';
    case 'written_off':
      return 'Dado de baja';
  }
}

/** Lo pendiente primero, lo más viejo arriba: es lo que lleva más esperando. */
export function partirCuarentena(entradas: readonly EntradaDeCuarentena[]): {
  pendientes: EntradaDeCuarentena[];
  decididas: EntradaDeCuarentena[];
} {
  const porFecha = (a: string | null, b: string | null) => (a ?? '').localeCompare(b ?? '');

  return {
    pendientes: entradas
      .filter((entrada) => entrada.status === 'pending')
      .sort((a, b) => porFecha(a.createdAt, b.createdAt)),
    decididas: entradas
      .filter((entrada) => entrada.status !== 'pending')
      .sort((a, b) => porFecha(b.decidedAt, a.decidedAt)),
  };
}

export function condicionDeVenta(condicion: LineaDelLibro['saleCondition']): string {
  return condicion === 'retained_prescription' ? 'Receta retenida' : 'Con receta';
}

/** Las columnas del CSV para quien inspecciona, en su orden. */
export const CABECERA_DEL_LIBRO = [
  'Fecha',
  'Pedido',
  'Comprobante',
  'Producto',
  'Cantidad',
  'Lote',
  'Condición',
  'N.º de receta',
  'Médico',
  'Atendió',
  'Anulada',
  'Sin receta',
];

/**
 * Las filas del CSV.
 *
 * La fecha va en día y hora **de Lima**, no la del equipo que exporta: el
 * libro se le enseña a un inspector, y la venta de las 23:30 no puede salir
 * con el día siguiente porque alguien lo bajó desde otro huso.
 */
export function filasDelLibro(lineas: readonly LineaDelLibro[]): (string | number | null)[][] {
  return lineas.map((linea) => [
    // Con el año siempre: el libro se guarda años.
    `${FECHA_DE_LIMA.format(new Date(linea.recordedAt))} ${hora(linea.recordedAt)}`,
    linea.orderNumber,
    linea.documentNumber,
    linea.productName,
    linea.quantity,
    linea.lots === '' ? 'Sin lote' : linea.lots,
    condicionDeVenta(linea.saleCondition),
    linea.prescriptionNumber,
    linea.prescriber,
    linea.checkedBy,
    // Anulada después de verse la receta: sigue en el libro, marcada.
    linea.cancelled ? 'Sí' : 'No',
    // Llegó sin red y sin la receta: sigue en el libro, marcada, para que el
    // químico la regularice.
    linea.withoutPrescription === true ? 'Sí' : 'No',
  ]);
}

// ── El libro de controlados (migración 273) ─────────────────────────────────

/** Un movimiento de algo que la ficha marca «Producto controlado». */
export interface MovimientoControlado {
  occurredAt: string;
  supplyId: string;
  supplyName: string;
  /** Los productos que salen de ese insumo, separados por coma. */
  productNames: string;
  kind: string;
  /** Con signo: lo que entra en positivo, lo que sale en negativo. */
  quantity: string;
  /** El saldo después de este movimiento. */
  balance: string;
  lotCode: string | null;
  document: string | null;
  performedBy: string | null;
  prescriptionNumber: string | null;
  prescriber: string | null;
}

/** El saldo de un controlado en el periodo. */
export interface SaldoControlado {
  supplyId: string;
  supplyName: string;
  productNames: string;
  opening: string;
  in: string;
  out: string;
  closing: string;
}

export const CABECERA_DE_CONTROLADOS = [
  'Fecha',
  'Producto',
  'Movimiento',
  'Cantidad',
  'Saldo',
  'Lote',
  'Documento',
  'N.º de receta',
  'Médico',
  'Responsable',
];

/** Las filas del CSV del libro de controlados, en día y hora de Lima. */
export function filasDeControlados(
  movimientos: readonly MovimientoControlado[],
  tipo: (kind: string) => string,
): (string | number | null)[][] {
  return movimientos.map((fila) => [
    `${FECHA_DE_LIMA.format(new Date(fila.occurredAt))} ${hora(fila.occurredAt)}`,
    fila.productNames === '' ? fila.supplyName : fila.productNames,
    tipo(fila.kind),
    fila.quantity,
    fila.balance,
    fila.lotCode ?? 'Sin lote',
    fila.document,
    fila.prescriptionNumber,
    fila.prescriber,
    fila.performedBy,
  ]);
}

// ── La cadena de frío (migración 273) ──────────────────────────────────────

/** Un equipo de frío con su última lectura. En décimas de grado: 2,0 °C es 20. */
export interface EquipoDeFrio {
  id: string;
  name: string;
  minTenths: number;
  maxTenths: number;
  lastReadAt: string | null;
  lastTenths: number | null;
  lastOutOfRange: boolean | null;
  /** Cuántas lecturas fuera de rango en los últimos siete días. */
  outOfRangeLast7Days: number;
}

export interface LecturaDeFrio {
  id: string;
  readAt: string;
  celsiusTenths: number;
  /** Con el rango que el equipo tenía cuando se leyó. */
  wasOutOfRange: boolean;
  recordedBy: string;
  note: string | null;
}

/**
 * «4,5» o «-18» → décimas de grado. Nulo si no es una temperatura: con más de
 * un decimal o fuera de lo creíble (−50 a 60 °C), que casi siempre es la coma
 * mal puesta.
 */
export function decimasDeGrado(texto: string): number | null {
  const limpio = texto.trim().replace(/\s*°?\s*c?$/i, '').replace(',', '.');
  if (!/^-?\d{1,2}(\.\d)?$/.test(limpio)) return null;
  const decimas = Math.round(Number(limpio) * 10);
  return decimas < -500 || decimas > 600 ? null : decimas;
}

/** 45 → «4,5 °C». */
export function grados(decimas: number): string {
  return `${(decimas / 10).toFixed(1).replace('.', ',')} °C`;
}

/** Si una temperatura queda fuera del rango del equipo. */
export function fueraDeRango(decimas: number, equipo: Pick<EquipoDeFrio, 'minTenths' | 'maxTenths'>): boolean {
  return decimas < equipo.minTenths || decimas > equipo.maxTenths;
}
